import React from 'react'
import { useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'

export interface LamictalScheduleLogObj {
  previousDosageMg: number
  newDosageMg: number
  action: string
  occurredAt: string
}

export interface LamictalScheduleObj {
  id: number
  patientId: number
  patientName: string
  providerId: number
  currentDosageMg: number
  nextDosageMg: number | null
  startedAt: string
  nextIncreaseAt: string | null
  terminatedAt: string | null
  isActive: boolean
  isAtCeiling: boolean
  logs: LamictalScheduleLogObj[]
}

export interface LamictalScheduleContextInterface {
  lamictalSchedules: LamictalScheduleObj[]
  activeSchedule: LamictalScheduleObj | null
  getPatientLamictalSchedules: (patientId: number) => Promise<void>
  startSchedule: (patientId: number) => Promise<void>
  resetDosage: (scheduleId: number, newDosageMg: number) => Promise<void>
  terminateSchedule: (scheduleId: number) => Promise<void>
}

export const LamictalScheduleContext =
  React.createContext<LamictalScheduleContextInterface | null>(null)

export const LamictalScheduleProvider = ({ children }: any) => {
  const { fetch } = useAxios()

  const [lamictalSchedules, setLamictalSchedules] = useState<
    LamictalScheduleObj[]
  >([])

  const activeSchedule = lamictalSchedules.find((s) => s.isActive) ?? null

  const getPatientLamictalSchedules = async (patientId: number) => {
    const { data, error } = await fetch({
      path: `LamictalSchedule/GetPatientLamictalSchedules?patientId=${patientId}`,
    })

    if (data) {
      setLamictalSchedules(data)
    } else if (error) {
      console.error('Error in getPatientLamictalSchedules.')
      setLamictalSchedules([])
    }
  }

  const startSchedule = async (patientId: number) => {
    const { data, error } = await fetch({
      path: `LamictalSchedule/StartSchedule?patientId=${patientId}`,
      methodType: 'POST',
    })

    if (data) {
      await getPatientLamictalSchedules(patientId)
    } else if (error) {
      throw new Error('Error in startSchedule.')
    }
  }

  const resetDosage = async (scheduleId: number, newDosageMg: number) => {
    const schedule = lamictalSchedules.find((s) => s.id === scheduleId)
    if (!schedule) return

    const { data, error } = await fetch({
      path: `LamictalSchedule/ResetDosage?scheduleId=${scheduleId}&newDosageMg=${newDosageMg}`,
      methodType: 'PUT',
    })

    if (data) {
      await getPatientLamictalSchedules(schedule.patientId)
    } else if (error) {
      throw new Error('Error in resetDosage.')
    }
  }

  const terminateSchedule = async (scheduleId: number) => {
    const schedule = lamictalSchedules.find((s) => s.id === scheduleId)
    if (!schedule) return

    const { data, error } = await fetch({
      path: `LamictalSchedule/TerminateSchedule?scheduleId=${scheduleId}`,
      methodType: 'PUT',
    })

    if (data) {
      await getPatientLamictalSchedules(schedule.patientId)
    } else if (error) {
      throw new Error('Error in terminateSchedule.')
    }
  }

  return (
    <LamictalScheduleContext.Provider
      value={{
        lamictalSchedules,
        activeSchedule,
        getPatientLamictalSchedules,
        startSchedule,
        resetDosage,
        terminateSchedule,
      }}
    >
      {children}
    </LamictalScheduleContext.Provider>
  )
}
